// ── Result view ────────────────────────────────────────────────────────────────
// Renders the try-on result image with action buttons. Supports multiview
// carousel (front / side / ...) with auto-slide when result_image is an array.

import { EXPAND_SVG, DOWNLOAD_SVG } from '../icons';
import { esc } from '../helpers';
import { TryStyleApiError } from '../../utils/fetcher';
import { prefersReducedMotion } from '../morph';
import type { ViewContext } from '../modal-types';

const CHEVRON_LEFT  = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>`;
const CHEVRON_RIGHT = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>`;

const LOGOUT_ICON = `<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>`;

const MOCK_VIDEO_URL = 'https://storage.googleapis.com/video-output-ts/video-output/13983892094732569601/sample_0.mp4?X-Goog-Algorithm=GOOG4-RSA-SHA256&X-Goog-Credential=trystyle-bvto%40trystyle-5b0b8.iam.gserviceaccount.com%2F20260504%2Fauto%2Fstorage%2Fgoog4_request&X-Goog-Date=20260504T112625Z&X-Goog-Expires=604800&X-Goog-SignedHeaders=host&X-Goog-Signature=95c353d757ddf01e36f63bd34b3829ec46668277eaf32640b8a800e480e5b38108088a2365abd55ebe6ab2a2932d646e8184a45408f7341cd2405e359a8a0051072c00e8cf6d01a1850f1f05bcfee99b4ee67ec80949ff96159719fe46fd8d17d70505a17303c2ff838d31f95cf0e403a8711ff5d173332bc0d976a38d40c8cf32fb9a0c280f1184bc38ffa8365eb3a1a0c228edc7643192b9d2300a8cd6ba7eac881dada154cf99d9594b7ef30748cfbbf338602cca949751c7e554ba1405ca8be3cc0151e3fc9ce004c4125493f608587e093d028cd50d5deedeb9697d991be52e5a0d2cfde2ba716f66863a4364554240d178fd849c71f9501a24d39e5bf9';
const CLOSE_X_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
const DOWNLOAD_SVG_VIDEO = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>`;

function openResultImageLightbox(
  images: string[],
  getStateUrl: () => string | null,
  hasMultiview: boolean,
  goTo: (idx: number) => void,
  getCurrentIdx: () => number,
): void {
  const initial = images[getCurrentIdx()] || getStateUrl();
  if (!initial) return;

  const overlay = document.createElement('div');
  overlay.className = 'ts-result-lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Try-on result');
  overlay.innerHTML = `
    <button type="button" class="ts-result-lightbox-close" aria-label="Close">${CLOSE_X_SVG}</button>
    ${hasMultiview ? `
    <button type="button" class="ts-result-lightbox-arrow ts-result-lightbox-prev" aria-label="Previous view">${CHEVRON_LEFT}</button>
    <button type="button" class="ts-result-lightbox-arrow ts-result-lightbox-next" aria-label="Next view">${CHEVRON_RIGHT}</button>
    ` : ''}
    <div class="ts-result-lightbox-inner">
      <img class="ts-result-lightbox-img" src="${esc(initial)}" alt="Try-on result" />
    </div>
  `;

  document.body.appendChild(overlay);
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const lbImg = overlay.querySelector<HTMLImageElement>('.ts-result-lightbox-img')!;

  const refresh = (): void => {
    const u = images[getCurrentIdx()] || getStateUrl();
    if (u) lbImg.src = u;
    if (!hasMultiview) return;
    const prevEl = overlay.querySelector<HTMLButtonElement>('.ts-result-lightbox-prev');
    const nextEl = overlay.querySelector<HTMLButtonElement>('.ts-result-lightbox-next');
    const i = getCurrentIdx();
    if (prevEl) prevEl.style.visibility = i === 0 ? 'hidden' : 'visible';
    if (nextEl) nextEl.style.visibility = i === images.length - 1 ? 'hidden' : 'visible';
  };

  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    document.body.style.overflow = prevOverflow;
    overlay.remove();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (!hasMultiview) return;
    // goTo clamps index; no extra bounds check needed here.
    if (e.key === 'ArrowLeft') {
      goTo(getCurrentIdx() - 1);
      refresh();
    }
    if (e.key === 'ArrowRight') {
      goTo(getCurrentIdx() + 1);
      refresh();
    }
  };

  document.addEventListener('keydown', onKey);

  overlay.querySelector('.ts-result-lightbox-close')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  overlay.querySelector('.ts-result-lightbox-prev')?.addEventListener('click', (e) => {
    e.stopPropagation();
    goTo(getCurrentIdx() - 1);
    refresh();
  });
  overlay.querySelector('.ts-result-lightbox-next')?.addEventListener('click', (e) => {
    e.stopPropagation();
    goTo(getCurrentIdx() + 1);
    refresh();
  });
}

function showFullscreenVideo(videoUrl: string, ctx: ViewContext): void {
  const { content, set } = ctx;
  const panel = content.querySelector('.ts-flow-panel');
  if (!panel) return;

  const overlay = document.createElement('div');
  overlay.className = 'ts-video-fullscreen';
  overlay.innerHTML = `
    <button class="ts-video-close" type="button" aria-label="Close video">${CLOSE_X_SVG}</button>
    <video src="${esc(videoUrl)}" autoplay controls playsinline></video>
    <div class="ts-video-toolbar">
      <button class="ts-btn-secondary" id="ts-download-video" type="button" style="width:auto;margin:0;background:rgba(255,255,255,.15);color:#fff;border-color:rgba(255,255,255,.3)">${DOWNLOAD_SVG_VIDEO} Download</button>
    </div>
  `;

  panel.appendChild(overlay);

  overlay.querySelector('.ts-video-close')?.addEventListener('click', () => {
    overlay.remove();
  });

  overlay.querySelector('#ts-download-video')?.addEventListener('click', () => {
    const a = document.createElement('a');
    a.href = videoUrl; a.download = 'trystyle-video.mp4';
    a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  });
}

export function renderResult(ctx: ViewContext): void {
  const { state, opts, content, client, set } = ctx;
  const isLoggedIn = client.auth.isAuthenticated() && !client.auth.getCurrentUser()?.isGuest;
  const user = client.auth.getCurrentUser();
  const isPaidUser = user && user.tier === 'paid';

  const images: string[] = (state.resultImages && state.resultImages.length > 0)
    ? state.resultImages
    : (state.resultUrl ? [state.resultUrl] : []);

  // Split delivery: while the side/back view is still generating, the front slide
  // is shown with a minimal "Generating side and back view…" indicator and the
  // multiview arrows / auto-slide / video button are suppressed until it lands.
  const isPending = state.viewsPending;
  const hasMultiview = images.length > 1 && !isPending;
  let currentIdx = 0;

  // Prev/next arrow buttons (overlaid on image) — always shown for multiview.
  const prevArrow = hasMultiview
    ? `<button class="ts-mv-arrow ts-mv-prev" id="ts-mv-prev" type="button" aria-label="Previous">${CHEVRON_LEFT}</button>`
    : '';
  const nextArrow = hasMultiview
    ? `<button class="ts-mv-arrow ts-mv-next" id="ts-mv-next" type="button" aria-label="Next">${CHEVRON_RIGHT}</button>`
    : '';

  // Sliding carousel track: all slides in a row + a clone of slide 0 appended
  // so the forward loop wraps seamlessly right-to-left.
  const slidesHTML = images
    .map((src, i) => `
      <div class="ts-result-slide"><img src="${esc(src)}" alt="Try-on result" loading="${i === 0 ? 'eager' : 'lazy'}" /></div>
    `)
    .join('');
  const cloneHTML = hasMultiview
    ? `<div class="ts-result-slide" aria-hidden="true"><img src="${esc(images[0] ?? '')}" alt="" loading="lazy" /></div>`
    : '';

  content.innerHTML = `
    ${ctx.headerHTML()}
    <div class="ts-mbody ts-flow-layout">
      <div class="ts-flow-panel">
        ${state.error ? `<div class="ts-error">${esc(state.error)}</div>` : ''}
        <div class="ts-result-wrap">
          <div class="ts-result-carousel" id="ts-result-carousel">
            <div class="ts-result-track" id="ts-result-track">${slidesHTML}${cloneHTML}</div>
          </div>
          ${isPending ? `
            <div class="ts-mv-overlay" aria-live="polite">
              <span class="ts-mv-dot"></span><span class="ts-mv-dot"></span><span class="ts-mv-dot"></span>
              <span class="ts-mv-text">Generating suggestive side &amp; back view...</span>
            </div>
          ` : ''}
          ${prevArrow}
          ${nextArrow}
          <div class="ts-result-actions">
            <button class="ts-result-action" id="ts-download" title="Download">${DOWNLOAD_SVG}</button>
            <button class="ts-result-action" id="ts-expand" title="Expand">${EXPAND_SVG}</button>
          </div>
        </div>

        <div class="ts-btn-row">
          <button class="ts-btn-primary" id="ts-retry" type="button">Try Again</button>
          ${!isPending && isPaidUser && state.resultUrl ? `
            <button class="ts-combo-cta" id="ts-generate-video" type="button">Generate Video</button>
          ` : ''}
        </div>

        ${state.multiviewError ? `<p class="ts-mv-error">${esc(state.multiviewError)}</p>` : ''}

        ${ctx.renderSizeCard(state.sizeRec, state.resolvedCategory ?? opts.category)}

        ${state.videoUrl ? `
          <button class="ts-btn-secondary" id="ts-view-video" type="button">View Video</button>
        ` : ''}

        <div class="ts-store-row">
          <span class="ts-store-text">Download our mobile app for better privacy controls.</span>
          <div class="ts-store-icons">
            <a href="https://play.google.com/store/apps/details?id=com.trystyle.fashion" target="_blank" rel="noopener" aria-label="Get TryStyle on Google Play">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" />
            </a>
            <a href="https://apps.apple.com/in/app/trystyle-fashion/id6756721230" target="_blank" rel="noopener" aria-label="Download TryStyle on the App Store">
              <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" />
            </a>
            ${isLoggedIn ? `
              <button class="ts-store-logout" id="ts-logout" type="button" aria-label="Logout" title="Logout">${LOGOUT_ICON}</button>
            ` : ''}
          </div>
        </div>
      </div>
    </div>
  `;

  const track = content.querySelector<HTMLElement>('#ts-result-track');
  const prevBtn = content.querySelector<HTMLButtonElement>('#ts-mv-prev');
  const nextBtn = content.querySelector<HTMLButtonElement>('#ts-mv-next');

  // Fade images in once loaded (natural ratio fills the card) and reflow the
  // modal so it hugs the result image with the edge padding.
  content.querySelectorAll<HTMLImageElement>('.ts-result-slide img').forEach((img) => {
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('ts-img-loaded');
      ctx.reflow();
    } else {
      img.addEventListener('load', () => {
        img.classList.add('ts-img-loaded');
        ctx.reflow();
      });
    }
  });

  // ── Slider ─────────────────────────────────────────────────────────────────
  // Transition is deliberately slow and eased. When a second (side/back) view
  // arrives, one reveal plays: front → slide left to side → 2s dwell → slide
  // back to front and stop. No auto-loop.
  const SLIDE_MS = 1100;
  const SLIDE_EASE = 'cubic-bezier(.4, 0, .2, 1)';
  const DWELL_MS = 2000;

  function setSlide(idx: number, animate = true): void {
    if (!track || images.length === 0) return;
    currentIdx = ((idx % images.length) + images.length) % images.length;
    track.style.transition = animate ? `transform ${SLIDE_MS}ms ${SLIDE_EASE}` : 'none';
    track.style.transform = `translateX(-${currentIdx * 100}%)`;
  }

  /** Slide forward to the trailing clone then reset to 0 — seamless wrap. */
  function slideToCloneAndReset(): void {
    if (!track) return;
    track.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASE}`;
    track.style.transform = `translateX(-${images.length * 100}%)`;
    currentIdx = 0;
    window.setTimeout(() => {
      if (!document.body.contains(track)) return;
      track.style.transition = 'none';
      track.style.transform = 'translateX(0%)';
      void track.offsetHeight; // force reflow before re-enabling transition
    }, SLIDE_MS + 40);
  }

  if (hasMultiview && track) {
    prevBtn?.addEventListener('click', () => {
      if (currentIdx === 0) setSlide(images.length - 1, false); // snap wrap
      else setSlide(currentIdx - 1);
    });
    nextBtn?.addEventListener('click', () => {
      if (currentIdx === images.length - 1) slideToCloneAndReset();
      else setSlide(currentIdx + 1);
    });

    // One-time reveal: front → side → dwell → back to front, then stop.
    if (!state.multiviewRevealed) {
      state.multiviewRevealed = true;
      if (!prefersReducedMotion()) {
        const run = (): void => {
          setSlide(1);
          window.setTimeout(() => {
            if (document.body.contains(track)) setSlide(0);
          }, SLIDE_MS + DWELL_MS);
        };
        const sideImg = content.querySelectorAll<HTMLImageElement>('.ts-result-slide img')[1];
        if (sideImg && !sideImg.complete) sideImg.addEventListener('load', run, { once: true });
        else run();
      }
    }
  }

  // ── Expand / download always follow the visible image ────────────────────
  content.querySelector('#ts-expand')?.addEventListener('click', () => {
    openResultImageLightbox(images, () => state.resultUrl, hasMultiview, setSlide, () => currentIdx);
  });

  content.querySelector('#ts-download')?.addEventListener('click', () => {
    void (async () => {
      const url = images[currentIdx] || state.resultUrl;
      const eventId = state.tryonEventId;
      const retailerId = opts.retailerId;

      if (eventId && retailerId) {
        try {
          const blob = await client.tryon.downloadResultImageBlob({
            eventId,
            retailerId,
            index: currentIdx,
          });
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = 'trystyle-result.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
          set({ error: null });
          return;
        } catch (err) {
          if (err instanceof TryStyleApiError) {
            set({ error: err.message });
            return;
          }
          set({
            error: err instanceof Error
              ? err.message
              : 'Download failed. Check your connection and try again.',
          });
          return;
        }
      }

      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.download = 'trystyle-result.png';
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    })();
  });

  content.querySelector('#ts-logout')?.addEventListener('click', () => {
    void (async () => {
      if (state.preview) URL.revokeObjectURL(state.preview);
      await client.auth.logout();
      set({
        phase: 'upload',
        file: null, preview: null, progress: 0,
        resultUrl: null, resultImages: null, videoUrl: null, error: null,
        viewsPending: false, multiviewError: null, multiviewRevealed: false,
        avatars: [], selectedAvatarId: null, sizeRec: null, resolvedCategory: null,
        comboOpen: false, comboError: null, comboSections: [], selectedComboProduct: null, comboCategories: null,
        processingLabel: state.processingLabel,
      });
    })();
  });

  content.querySelector('#ts-retry')?.addEventListener('click', () => {
    if (state.preview) URL.revokeObjectURL(state.preview);
    const user = client.auth.getCurrentUser();
    const comboReset = {
      comboOpen: false, comboError: null, comboSections: [], selectedComboProduct: null, comboCategories: null,
    };
    if (user && !user.isGuest) {
      client.avatars.list(1, 20).then(({ avatars }) => {
        set({
          phase: 'avatar-preview', file: null, preview: null,
          error: null, resultUrl: null, resultImages: null, videoUrl: null, sizeRec: null, resolvedCategory: null,
          viewsPending: false, multiviewError: null, multiviewRevealed: false,
          avatars, selectedAvatarId: avatars.find(a => a.isPrimary)?.id ?? avatars[0]?.id ?? null,
          ...comboReset,
        });
      }).catch(() => {
        set({ phase: 'upload', file: null, preview: null, error: null, resultUrl: null, resultImages: null, videoUrl: null, sizeRec: null, resolvedCategory: null, viewsPending: false, multiviewError: null, multiviewRevealed: false, ...comboReset });
      });
    } else {
      set({ phase: 'upload', file: null, preview: null, error: null, resultUrl: null, resultImages: null, videoUrl: null, sizeRec: null, resolvedCategory: null, viewsPending: false, multiviewError: null, multiviewRevealed: false, ...comboReset });
    }
  });

  // ── Video generation (paid users only) ────────────────────────────────────
  content.querySelector('#ts-generate-video')?.addEventListener('click', async () => {
    const videoBtn = content.querySelector<HTMLButtonElement>('#ts-generate-video');
    if (!videoBtn) return;

    try {
      videoBtn.disabled = true;
      videoBtn.textContent = 'Generating...';

      let videoUrl: string | null = null;

      if (client.__devMockVideo) {
        await new Promise(r => setTimeout(r, 2000));
        videoUrl = MOCK_VIDEO_URL;
      } else {
        if (!state.resultGcsPath) {
          throw new Error('Missing result GCS path for video generation');
        }

        const videoJob = await client.tryon.generateVideoAndWait({
          tryonEventId: state.tryonEventId ?? undefined,
          resultGcsPath: state.resultGcsPath,
          avatarId: state.selectedAvatarId ?? undefined,
          productId: opts.productId,
        });

        if (!videoJob.video_url) {
          throw new Error('Video generation completed but no video URL returned');
        }
        videoUrl = videoJob.video_url;
      }

      set({ videoUrl });
      videoBtn.textContent = 'Generate Video';
    } catch (error) {
      console.error('Video generation failed:', error);
      const errorMsg = error instanceof Error ? error.message : 'Video generation failed';
      alert(`Error: ${errorMsg}`);
      videoBtn.textContent = 'Generate Video';
    } finally {
      videoBtn.disabled = false;
    }
  });

  // ── View video button ─────────────────────────────────────────────────────
  content.querySelector('#ts-view-video')?.addEventListener('click', () => {
    if (state.videoUrl) {
      showFullscreenVideo(state.videoUrl, ctx);
    }
  });
}
